// app/src/lib/cashflow/subscriptions.js — software subscription catalog (US-3.8).
//
// Subscriptions are RecurringItems with category === 'software'. Renewal alert
// fires at `renewalAlertDays` before `nextDate`. "Phantom" subscriptions are
// user-flagged — there is no usage API.

import { db } from '../db.js';
import { requireUserId } from '../procurement/util.js';
import { get as getPrefs } from './prefs.js';
import { daysBetween, isoToday } from './util.js';

export async function list() {
  const userId = await requireUserId();
  const all = await db.recurringItems.where('userId').equals(userId).toArray();
  return all
    .filter((r) => (r.category || '').toLowerCase() === 'software')
    .map((r) => ({
      ...r,
      perSeatCents: r.seatCount && r.seatCount > 0 ? Math.round((r.amountCents || 0) / r.seatCount) : null,
    }))
    .sort((a, b) => a.nextDate.localeCompare(b.nextDate));
}

/** Returns subscriptions whose next renewal is within the alert window. */
export async function imminentRenewals() {
  const userId = await requireUserId();
  const prefs = await getPrefs();
  const today = isoToday();
  const subs = await list();
  return subs.filter((s) => {
    const days = daysBetween(s.nextDate, today);
    const window = s.renewalAlertDays ?? prefs.renewalAlertDays ?? 14;
    return days >= 0 && days <= window;
  });
}

/** Toggle the user-managed "phantom" flag. */
export async function flagPhantom(recurringId, phantom = true) {
  const userId = await requireUserId();
  const r = await db.recurringItems.get(recurringId);
  if (!r || r.userId !== userId) throw new Error('subscription: not found');
  r.isPhantom = phantom ? 1 : 0;
  await db.recurringItems.put(r);
  return r;
}

/**
 * Run after a scan: emit info-level alerts for subscriptions renewing within
 * their per-subscription `renewalAlertDays` window (default 14). Deduped.
 */
export async function checkRenewalAlerts() {
  const userId = await requireUserId();
  const due = await imminentRenewals();
  const emitted = [];
  for (const s of due) {
    const existing = await db.alerts.where('userId').equals(userId)
      .filter((a) => a.kind === 'SUBSCRIPTION_RENEWAL' && a.meta?.recurringId === s.recurringId && a.meta?.nextDate === s.nextDate)
      .first();
    const row = {
      alertId: existing?.alertId || crypto.randomUUID(),
      userId,
      severity: 'info',
      kind: 'SUBSCRIPTION_RENEWAL',
      title: `${s.label || s.vendorName || 'Subscription'} renews ${s.nextDate}`,
      body: `$${((s.amountCents || 0) / 100).toFixed(2)} via ${s.cadence || 'recurring'}.`,
      createdAt: existing?.createdAt || Date.now(),
      dismissed: existing?.dismissed ?? 0,
      meta: { recurringId: s.recurringId, nextDate: s.nextDate },
    };
    await db.alerts.put(row);
    emitted.push(row);
  }
  return emitted;
}
